// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React, { useState } from 'react';

import { addTruthyParamsToUrl } from '@zapier/url-utils';
import {
  Animation,
  Avatar,
  Colors,
  FloatingBox,
  Icon,
  TooltipWrapper,
  createUniqueClassName,
  kebabCaseAriaAttrs,
  useClickOutside,
} from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import UniversalNavLink from '../UniversalNavLink';
import { AccountsMenuLinks } from '../constants';

import type { UniversalNavLinkType } from '@zapier/design-system-patterns';

/** @jsx isolateComponent */

type Account = {
  id: number,
  name: string,
  imageUrl?: string,
};

export type Props = {|
  /**
   * List of user's accounts to render in the accounts menu.
   */
  accounts: Array<Account>,
  /**
   * Id of currently active user account.
   */
  currentAccountId?: number,
  /**
   * Label describing the accounts menu.
   */
  menuLabel: string,
  /**
   * Array of links to render in the accounts menu.
   */
  menuLinks: Array<UniversalNavLinkType>,
  /**
   * Handler called when an account menu item is clicked.
   */
  onAccountClick?: (
    account: Account,
    event: SyntheticMouseEvent<HTMLElement>
  ) => void,
  /**
   * How large the menu renders, especiall the `Avatar`.
   */
  size: 'small' | 'medium',
  /**
   * The URL to route to when switching accounts.
   * Defaults to the current URL implicitly via an empty string.
   */
  accountSwitcherUrl: string,
|};

const detailsClassName = createUniqueClassName(__filename, 'details');
const detailsSelector = `.${detailsClassName}`;

const Styles = labelStyles(__filename, {
  root: {
    position: 'relative',
  },
  accounts: {
    borderBottom: `1px solid ${Colors.grey100}`,
    marginBottom: 10,
    paddingBottom: 10,
  },
  summary: {
    position: 'relative',
    display: 'block',
    cursor: 'pointer',
    transition: Animation.transitionValue,
    // This is the ring around the avatar. It doesn't take up
    // any layout space.
    '&::before': {
      content: '""',
      pointerEvents: 'none',
      display: 'block',
      position: 'absolute',
      top: -5,
      right: -5,
      bottom: -5,
      left: -5,
      border: '3px solid transparent',
      borderRadius: '100px',
    },
    '&:hover::before, &:focus::before': {
      borderColor: Colors.grey200,
    },
    [`${detailsSelector}[open] &::before`]: {
      borderColor: Colors.blue500,
    },
    '&::-webkit-details-marker': {
      display: 'none',
    },
  },
  avatarIconContainer: {
    position: 'absolute',
    backgroundColor: Colors.white,
    borderRadius: '50%',
    bottom: 0,
    // We want the icon slightly outside the container
    right: -9,
    width: 18,
    height: 18,
  },
  avatar: {
    position: 'relative',
  },
  menuContainer: {
    padding: 10,
    width: 300,
  },
});

// Renders a list of accounts as menu items
const renderAccountSwitchMenu = (props: Props) => (
  <div css={Styles.accounts}>
    {props.accounts.map(account => {
      const isCurrentAccount = account.id === props.currentAccountId;
      return (
        <UniversalNavLink
          href={addTruthyParamsToUrl(props.accountSwitcherUrl, {
            context: account.id,
          })}
          isSelected={isCurrentAccount}
          key={account.id}
          onClick={event => {
            if (props.onAccountClick) {
              props.onAccountClick(account, event);
            }
          }}
          renderBeforeText={() => (
            <div css={Styles.avatar}>
              <Avatar
                name={account.name}
                shouldLazilyLoad={false}
                size={40}
                url={account.imageUrl}
              />
              {isCurrentAccount && (
                <div css={Styles.avatarIconContainer}>
                  <Icon color="blue500" name="formCheckCircle" size={18} />
                </div>
              )}
            </div>
          )}
          text={account.name}
        />
      );
    })}
  </div>
);

const renderMenuLinks = (props: Props) =>
  props.menuLinks.map(menuLink => (
    <UniversalNavLink
      href={menuLink.href}
      icon={menuLink.icon}
      key={menuLink.href}
      text={menuLink.text}
    />
  ));

// This is fake placeholder account data for when no current account
// is found, which should only happen when loading accounts. This fallback
// account allows `AccountsMenu` to fully render so the UI is more
// incrementally updated instead of pieces of it conditionally rendering.
const fallbackAccount: Account = {
  id: -1,
  name: '⋯',
};

/**
 * Renders a menu with accounts and account action links.
 */
const AccountsMenu = (props: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [setClickOutsideNode] = useClickOutside({
    onClickOutside: () => setIsMenuOpen(false),
  });

  const currentAccount: Account =
    props.accounts.find(account => account.id === props.currentAccountId) ||
    fallbackAccount;

  const toggleMenu = (event: SyntheticMouseEvent<HTMLElement>) => {
    event.preventDefault();
    setIsMenuOpen(!isMenuOpen);
  };

  // The native implementation of details handles keyboard events by default.
  /* eslint-disable jsx-a11y/click-events-have-key-events */
  return (
    <div css={Styles.root}>
      <details
        className={detailsClassName}
        open={isMenuOpen}
        ref={node => setClickOutsideNode(node)}
      >
        <summary
          aria-haspopup="menu"
          aria-label={props.menuLabel}
          css={Styles.summary}
          onClick={toggleMenu}
          role="button"
          tabIndex={0}
        >
          <TooltipWrapper
            content={currentAccount.name}
            isBlock={true}
            position="west"
            shouldCorrectPosition={false}
          >
            {({ childProps }) => (
              <div {...kebabCaseAriaAttrs(childProps)}>
                <Avatar
                  isBlock={true}
                  name={currentAccount.name}
                  shouldLazilyLoad={false}
                  size={
                    {
                      small: 30,
                      medium: 40,
                    }[props.size]
                  }
                  url={currentAccount.imageUrl}
                />
              </div>
            )}
          </TooltipWrapper>
        </summary>
        <div role="menu">
          <FloatingBox align="right" position="south">
            <div css={Styles.menuContainer}>
              {props.accounts.length > 1 && renderAccountSwitchMenu(props)}
              {renderMenuLinks(props)}
            </div>
          </FloatingBox>
        </div>
      </details>
    </div>
  );
};

AccountsMenu.defaultProps = {
  menuLabel: 'Accounts Settings',
  menuLinks: AccountsMenuLinks,
  size: 'medium',
  accountSwitcherUrl: '',
};

export default AccountsMenu;
